/**
 * MacroContextStrip — Feb 2026 (10-card expansion + "pop" styling)
 *
 * A responsive grid of 10 macro indicators pulled from `/api/macro`.
 * Data sources (zero new API calls beyond the existing endpoint):
 *   • treasury_yields.latest         → 10Y Treasury, 2Y Treasury,
 *                                       Yield Curve (10Y - 2Y)
 *   • inflation.latest               → CPI YoY, Core PCE YoY
 *   • inflation_expectations.latest  → 10Y Breakeven, 5Y5Y Forward
 *   • labor_market.latest            → Unemployment, Labor Force Participation
 *   • sp500_dividend_yield.latest    → S&P 500 Dividend Yield (via SPY ETF)
 *
 * Layout:
 *   • Phone (default): 2-row horizontal swipe strip. Cards flow into
 *                      two rows and scroll off-screen to the right;
 *                      user swipes left/right (snap points). Right
 *                      edge intentionally bleeds past the viewport so
 *                      the "there's more" cue is unmistakable.
 *   • sm+ (≥640px):    5 columns → 2 rows tall (normal grid)
 *   • lg+ (≥1024px):   10 columns → 1 row across (normal grid)
 *
 * Styling (Feb 2026 polish):
 *   • Every card floats with a soft drop shadow + hairline border to
 *     visibly separate from the page background in both light and
 *     dark themes.
 *   • Yield Curve card is state-colored: RED when inverted (spread<0),
 *     AMBER when flat/near-inversion (0 ≤ spread < 0.10%), and normal
 *     otherwise. Icon inherits the state color for at-a-glance recognition.
 *   • Primary value is bumped to text-base semibold so the number is
 *     the visual hero, labels drop to text-[9px] muted.
 *   • Cards hover-lift 1px with a slightly stronger shadow for
 *     tactile feedback.
 *
 * Failure-mode: if the API doesn't respond in 3s or a specific field is
 * missing, that card silently drops (no fallback junk on-screen).
 */
import { useEffect, useState, useRef } from 'react';
import SwipeHint, { useSwipeHint } from '@/components/SwipeHint';
import axios from 'axios';
import SectorExamplesModal from '@/components/SectorExamplesModal';
import {
  TrendingUp, DollarSign, Users, Target,
  Landmark, Activity, LineChart, Anchor, UserCheck, PiggyBank,
  ArrowUp, ArrowDown, Minus, Sparkles, X, ChevronRight, CheckCircle2, Ban,
} from 'lucide-react';

// Sector playbooks — which sectors historically lead / lag in each macro
// regime. Each item is [sector, short why]. Keyed so both the historical
// regime matcher and the macro-story fallback can reference them.
const SECTOR_PLAYBOOKS = {
  hotInflation: {
    favor: [
      ['Energy', 'commodity prices outrun inflation', ['Energy']],
      ['Financials', 'wider net interest margins', ['Financial Services']],
      ['Consumer Staples', 'pricing power passes costs on', ['Consumer Defensive']],
      ['Healthcare', 'steady demand in any cycle', ['Healthcare']],
      ['Materials & Industrials', 'real-asset pricing holds', ['Basic Materials', 'Industrials']],
    ],
    avoid: [
      ['Long-duration Growth & Tech', 'higher rates shrink future earnings value', ['Technology']],
      ['REITs & Utilities', 'heavy debt, compete with 5%+ Treasuries', ['Real Estate', 'Utilities']],
    ],
  },
  stagflation: {
    favor: [
      ['Energy', 'the only sector with real earnings growth', ['Energy']],
      ['Consumer Staples', 'necessities keep selling', ['Consumer Defensive']],
      ['Gold & Materials', 'hard-asset inflation hedge', ['Basic Materials']],
      ['Healthcare', 'demand ignores the cycle', ['Healthcare']],
    ],
    avoid: [
      ['Consumer Discretionary', 'squeezed real incomes', ['Consumer Cyclical']],
      ['Financials', 'credit losses outweigh margins', ['Financial Services']],
      ['High-payout REITs & BDCs', 'dividend cuts likely', ['Real Estate']],
    ],
  },
  goldilocks: {
    favor: [
      ['Technology', 'earnings growth with cheap capital', ['Technology']],
      ['Consumer Discretionary', 'confident spenders', ['Consumer Cyclical']],
      ['Industrials', 'capex cycle running', ['Industrials']],
      ['Dividend Growers', 'compounding at its best'],
    ],
    avoid: [
      ['Cash & short T-bills', 'yield lags total returns'],
      ['Over-hedged defensives', 'paying for insurance you don\u2019t need'],
    ],
  },
  recession: {
    favor: [
      ['Consumer Staples', 'JNJ / PG / KO-style cash machines', ['Consumer Defensive']],
      ['Healthcare', 'non-cyclical demand', ['Healthcare']],
      ['Utilities', 'regulated earnings, falling rates help', ['Utilities']],
      ['Cash-rich Dividend Kings', 'can raise through the downturn'],
    ],
    avoid: [
      ['Cyclicals & Industrials', 'earnings collapse first', ['Consumer Cyclical', 'Industrials']],
      ['mREITs & BDCs', 'dividend suspensions', ['Real Estate']],
      ['Small-cap high-yield', 'refinancing risk'],
    ],
  },
  neutral: {
    favor: [
      ['Dividend Growers', 'quality compounding beats sector bets', null],
      ['Healthcare', 'steady demand, reasonable multiples', ['Healthcare']],
      ['Consumer Staples', 'pricing power without cycle risk', ['Consumer Defensive']],
      ['Financials', 'benefit from a normal, upward-sloping curve', ['Financial Services']],
      ['Industrials', 'capex + reshoring tailwinds', ['Industrials']],
    ],
    avoid: [
      ['Speculative high-yield', 'coverage thin, cuts come first', null],
      ['Leveraged REITs & mREITs', 'rate volatility hits book value', ['Real Estate']],
    ],
  },
  rateHikeTop: {
    favor: [
      ['Consumer Staples', 'led the last pivot (+14%)', ['Consumer Defensive']],
      ['Utilities', 'rally as yields roll over', ['Utilities']],
      ['REITs', 'duration becomes a tailwind', ['Real Estate']],
      ['Dividend Aristocrats', 'crushed SPY after the pivot'],
    ],
    avoid: [
      ['Financials', 'margins compress as rates fall', ['Financial Services']],
      ['Energy', 'late-cycle demand fades', ['Energy']],
    ],
  },
};


// Sep 2026 — Bucket labels + per-chip impact matrix used by the
// "What it means for YOUR portfolio" section. Each entry maps a card +
// direction to the holding buckets that benefit / face a headwind, with
// a 3-8 word reason. `dir` is 'up' | 'down' | 'flat' | 'red' | 'amber' | 'green'.
const BUCKET_LABELS = {
  reits: 'REITs', bdcs: 'BDCs', banks: 'Banks', financials: 'Financials', utilities: 'Utilities',
  energy: 'Energy', staples: 'Consumer Staples', tech: 'Technology', consumer: 'Consumer Cyclical',
  leveraged: 'Leveraged ETFs', cefs: 'Closed-End Funds', clos: 'CLO ETFs', healthcare: 'Healthcare',
  industrials: 'Industrials', materials: 'Materials', mmf: 'Money Market Funds',
};
const IMPACT_MATRIX = {
  fed_funds: {
    up: { benefit: { banks: 'wider net interest margins', bdcs: 'floating-rate NII rises', clos: 'SOFR-linked coupons reset higher', mmf: 'yield steps up immediately', financials: 'higher rates on float' },
          headwind: { reits: 'refinancing costs up, cap rates pressured', utilities: 'debt-heavy, competes with cash yields', cefs: 'leverage cost rises, NAV pressure', leveraged: 'borrow cost drag compounds', tech: 'future earnings discounted harder', consumer: 'credit-driven spending slows' } },
    down: { benefit: { reits: 'cheaper debt, relative yield improves', utilities: 'bond-proxy rally', cefs: 'leverage cost falls, discounts narrow', tech: 'long-duration growth re-rates', consumer: 'credit loosens', leveraged: 'carry cost eases' },
            headwind: { banks: 'NIM compression', bdcs: 'floating-rate NII falls', clos: 'coupons reset lower', mmf: 'yield drops within weeks' } },
  },
  treasury_10y: {
    up: { benefit: { banks: 'steeper curve, wider spreads', energy: 'inflation-hedge bid', financials: 'higher reinvestment yields' },
          headwind: { reits: 'long-duration income competes with risk-free', utilities: 'bond-proxy sell-off', tech: 'higher discount rate on growth', cefs: 'NAV marks lower on bond sleeves' } },
    down: { benefit: { reits: 'refinancing + relative-yield tailwind', utilities: 'bond-proxy rally', tech: 'growth re-rates higher', cefs: 'bond NAVs mark up' },
            headwind: { banks: 'flatter curve squeezes spreads', energy: 'growth-scare read-through' } },
  },
  treasury_2y: {
    up: { benefit: { banks: 'more hikes priced — margins widen', bdcs: 'floating-rate income up', clos: 'coupon reset higher', mmf: 'yield follows the 2Y' },
          headwind: { reits: 'rate-sensitive', utilities: 'rate-sensitive', consumer: 'borrowing costs bite', leveraged: 'carry cost up' } },
    down: { benefit: { reits: 'cuts priced in', utilities: 'cuts priced in', tech: 'growth re-rates', consumer: 'cheaper credit' },
            headwind: { banks: 'NIM outlook fades', bdcs: 'NII outlook fades', clos: 'coupons will reset lower', mmf: 'yield will follow lower' } },
  },
  yield_curve: {
    red: { benefit: { staples: 'defensive earnings hold up', healthcare: 'non-cyclical demand', utilities: 'regulated cash flows', mmf: 'park cash at the short end' },
           headwind: { banks: 'borrow short / lend long inverts', consumer: 'recession-sensitive', industrials: 'capex cycle rolls over', bdcs: 'credit losses rise in downturns', leveraged: 'volatility amplifies drawdowns', materials: 'demand fades' } },
    green: { benefit: { banks: 'classic steepener — wider NIM', industrials: 'growth signal', consumer: 'expansion read', energy: 'cyclical demand', materials: 'cyclical demand', financials: 'risk-on' },
             headwind: { utilities: 'lag in risk-on tape', staples: 'defensives lag' } },
    amber: { benefit: { staples: 'late-cycle defensives', healthcare: 'late-cycle defensives' }, headwind: { banks: 'flat curve = thin spreads', consumer: 'late-cycle caution' } },
  },
  cpi: {
    red: { benefit: { energy: 'commodity prices outrun inflation', materials: 'real-asset pricing', staples: 'pricing power passes costs on', reits: 'rents index to inflation (selectively)', clos: 'floating-rate hedge' },
           headwind: { tech: 'higher-for-longer discounting', utilities: 'costs up, regulated pricing lags', consumer: 'real incomes squeezed', cefs: 'fixed-coupon sleeves lose real value', mmf: 'real yield eroded' } },
    green: { benefit: { tech: 'disinflation → lower rates', utilities: 'rate relief', reits: 'rate relief', consumer: 'real incomes recover', cefs: 'bond sleeves rally' },
             headwind: { energy: 'commodity bid fades', materials: 'pricing power fades' } },
    amber: { benefit: { staples: 'steady pricing power', healthcare: 'steady demand' }, headwind: { utilities: 'cost pass-through lag' } },
  },
  unemployment: {
    red: { benefit: { staples: 'necessities keep selling', healthcare: 'non-cyclical', utilities: 'regulated earnings', mmf: 'safety of cash' },
           headwind: { consumer: 'discretionary spending drops first', banks: 'credit losses climb', bdcs: 'portfolio defaults rise', industrials: 'orders slow', leveraged: 'drawdown risk', financials: 'credit cycle turns' } },
    green: { benefit: { consumer: 'confident spenders', banks: 'loan growth + low losses', industrials: 'hiring = capex', financials: 'risk-on', bdcs: 'benign credit' },
             headwind: { utilities: 'defensives lag in expansions', staples: 'defensives lag' } },
  },
};
IMPACT_MATRIX.pce_core = IMPACT_MATRIX.cpi;
IMPACT_MATRIX.breakeven_10y = IMPACT_MATRIX.cpi;
IMPACT_MATRIX.forward_5y5y = IMPACT_MATRIX.cpi;
IMPACT_MATRIX.lfpr = IMPACT_MATRIX.unemployment;

const PortfolioImpactLists = ({ benefit, headwind }) => (
  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-[11px]" data-testid="macro-card-portfolio-impact">
    {[['Benefit', benefit, 'emerald', CheckCircle2, 'macro-impact-benefit'], ['Headwind', headwind, 'rose', Ban, 'macro-impact-headwind']].map(([title, rows, tone, Icon, tid]) => (
      <div key={title} data-testid={tid}>
        <div className={`flex items-center gap-1 font-bold uppercase tracking-wider text-[10px] ${tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
          <Icon className="w-3 h-3" />{title}
        </div>
        {rows.length === 0 ? (
          <div className="text-slate-400 dark:text-slate-500 mt-0.5">None of your holdings sit here.</div>
        ) : (
          <ul className="mt-0.5 space-y-0.5">
            {rows.map(([bucket, why, tickers, count]) => (
              <li key={bucket} className="leading-snug" data-testid={`macro-impact-${bucket}`}>
                <span className="font-semibold text-slate-800 dark:text-slate-100">{BUCKET_LABELS[bucket]}</span>
                <span className="font-mono text-[10px] text-slate-500 dark:text-slate-400"> {tickers.join(', ')}{count > tickers.length ? ` +${count - tickers.length}` : ''}</span>
                <span className="text-slate-500 dark:text-slate-400"> — {why}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    ))}
  </div>
);

const PlaybookList = ({ title, tone, icon: Icon, items, testId, onPick }) => (
  <div data-testid={testId} className="min-w-0">
    <div className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider ${tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
      <Icon className="w-3.5 h-3.5" />
      {title}
    </div>
    <ul className="mt-1 space-y-0.5">
      {items.map((item) => {
        const [sector, why, dbSectors] = item;
        const slug = sector.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return (
          <li key={sector} className="flex items-start gap-1.5 text-[11px] leading-snug" data-testid={`${testId}-item`}>
            <span className={`mt-[6px] w-1 h-1 rounded-full shrink-0 ${tone === 'emerald' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            <span className="min-w-0">
              {dbSectors ? (
                <button
                  type="button"
                  onClick={() => onPick(item, tone)}
                  className="font-semibold text-slate-800 dark:text-slate-100 underline decoration-dotted decoration-slate-400 underline-offset-2 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
                  title={`See top-quality ${sector} examples`}
                  data-testid={`playbook-sector-${slug}`}
                >
                  {sector}
                </button>
              ) : (
                <span className="font-semibold text-slate-800 dark:text-slate-100">{sector}</span>
              )}
              <span className="text-slate-500 dark:text-slate-400"> — {why}</span>
            </span>
          </li>
        );
      })}
    </ul>
  </div>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feb 2026 — themed accent for each card. Keeps every card visually
// distinct without overwhelming the strip. Colors chosen to survive
// both light and dark themes (Tailwind auto-swaps via dark: prefix).
const ACCENTS = {
  slate: 'text-slate-500 dark:text-slate-400',
  amber: 'text-amber-600 dark:text-amber-400',
  blue: 'text-blue-600 dark:text-blue-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  rose: 'text-rose-600 dark:text-rose-400',
  violet: 'text-violet-600 dark:text-violet-400',
  cyan: 'text-cyan-600 dark:text-cyan-400',
  fuchsia: 'text-fuchsia-600 dark:text-fuchsia-400',
};

// Feb 2026 — tiny inline SVG sparkline. Renders a smooth path through
// the given numeric points; auto-scales Y based on min/max, no axes,
// no labels — pure at-a-glance "arc" of the last 30 days / 12 months.
// Stroke color inherits from `currentColor` so the parent controls it
// via a Tailwind text-* class. Optional `endDot` = 'good' | 'bad' |
// 'flat' — renders a small colored circle at the last point so the
// user knows if the trend is heading in a favorable direction for
// this specific metric (context-aware, since "up" means different
// things per card — e.g., rising unemployment is bad, rising
// participation is good).
function Sparkline({ points, width = 60, height = 14, className = '', endDot = null }) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const nums = points.filter((v) => typeof v === 'number' && isFinite(v));
  if (nums.length < 2) return null;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const range = max - min || 1;
  const stepX = width / (nums.length - 1);
  const path = nums
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * height;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const lastX = (nums.length - 1) * stepX;
  const lastY = height - ((nums[nums.length - 1] - min) / range) * height;
  const dotFill = endDot === 'good'
    ? '#10b981' // emerald-500
    : endDot === 'bad'
      ? '#f43f5e' // rose-500
      : endDot === 'flat'
        ? '#94a3b8' // slate-400
        : null;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`overflow-visible ${className}`}
      aria-hidden="true"
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {dotFill && (
        <circle
          cx={lastX}
          cy={lastY}
          r="2.25"
          fill={dotFill}
          stroke="white"
          strokeWidth="0.75"
        />
      )}
    </svg>
  );
}

function Cell({ icon: Icon, label, value, sub, testId, tooltip, accent = 'slate', stateTint = null, delta = null, deltaLabel = null, sparkline = null, polarity = 'up-bad', onTap = null, cardKey = null }) {
  // polarity — what does "rising" mean for this specific metric?
  //   'up-bad'  → rising is BAD (default; most macro metrics work this
  //               way: rising inflation, rising unemployment, rising
  //               rates all hurt dividend investors)
  //   'up-good' → rising is GOOD (yield curve steepening, labor force
  //               participation rising, etc.)
  // stateTint (optional): 'red' | 'amber' | 'green' → overrides the
  // base border + adds a soft glow to flag critical macro states.
  //   red   = danger / hawkish / recession warning
  //   amber = elevated / caution / near-threshold
  //   green = healthy / Fed target hit / dovish anchor
  //
  // delta (optional): {value: number, direction: 'up'|'down'|'flat'}
  // Renders a small arrow + magnitude pill so the card shows if the
  // reading is rising or falling since the last observation. Formatted
  // as absolute percentage points (e.g., "▲ 0.12" for a +0.12pp move).
  const baseCard = `
    group flex items-center gap-1.5 px-2 py-1.5 rounded-lg min-w-0
    bg-white dark:bg-slate-900/60
    border border-slate-200 dark:border-slate-700/60
    shadow-[0_4px_12px_-2px_rgba(15,23,42,0.14),0_2px_4px_-1px_rgba(15,23,42,0.06)]
    dark:shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_2px_8px_-2px_rgba(0,0,0,0.6)]
    hover:-translate-y-0.5
    hover:shadow-[0_8px_20px_-4px_rgba(15,23,42,0.18),0_4px_8px_-2px_rgba(15,23,42,0.08)]
    dark:hover:shadow-[0_1px_0_0_rgba(255,255,255,0.06)_inset,0_4px_12px_-2px_rgba(0,0,0,0.7)]
    transition-all duration-150 cursor-help
    snap-start sm:snap-align-none
  `;
  const tintClass =
    stateTint === 'red'
      ? 'border-rose-400 dark:border-rose-500/60 bg-rose-100/90 dark:bg-rose-500/[0.08] shadow-[0_0_18px_-4px_rgba(244,63,94,0.55)]'
      : stateTint === 'amber'
        ? 'border-amber-400 dark:border-amber-500/60 bg-amber-100/90 dark:bg-amber-500/[0.08] shadow-[0_0_18px_-4px_rgba(245,158,11,0.5)]'
        : stateTint === 'green'
          ? 'border-emerald-400 dark:border-emerald-500/60 bg-emerald-100/90 dark:bg-emerald-500/[0.08] shadow-[0_0_18px_-4px_rgba(16,185,129,0.5)]'
          : '';
  const iconColorClass =
    stateTint === 'red'
      ? 'text-rose-600 dark:text-rose-400'
      : stateTint === 'amber'
        ? 'text-amber-600 dark:text-amber-400'
        : stateTint === 'green'
          ? 'text-emerald-600 dark:text-emerald-400'
          : ACCENTS[accent] || ACCENTS.slate;
  // Trend arrow color is intentionally NEUTRAL — a rising unemployment
  // rate is "bad" but a rising treasury yield can be "good or bad"
  // depending on context. We don't want the arrow to fight the state
  // tint. Green/red arrows show DIRECTION only (up/down), and the
  // interpretation lives in the tooltip.
  const ArrowIcon = delta?.direction === 'up' ? ArrowUp : delta?.direction === 'down' ? ArrowDown : Minus;
  const arrowColor = delta?.direction === 'up'
    ? 'text-emerald-600 dark:text-emerald-400'
    : delta?.direction === 'down'
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-slate-400 dark:text-slate-500';
  return (
    <div
      className={`${baseCard} ${tintClass}`}
      data-testid={testId}
      title={tooltip}
      onClick={onTap ? () => onTap({ label, tooltip, value, sub, cardKey }) : undefined}
      role={onTap ? 'button' : undefined}
      tabIndex={onTap ? 0 : undefined}
      onKeyDown={onTap ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap({ label, tooltip, value, sub, cardKey }); } } : undefined}
    >
      <Icon className={`w-4 h-4 shrink-0 ${iconColorClass}`} />
      <div className="leading-tight min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-wide text-slate-500 dark:text-slate-400 truncate font-medium">
          {label}
        </div>
        <div className="flex items-baseline gap-1 min-w-0">
          <div className="font-mono text-sm font-bold text-slate-900 dark:text-slate-50 tabular-nums shrink-0">
            {value}
          </div>
          {delta && delta.value != null && (
            <div
              className={`flex items-center gap-0.5 text-[9px] font-semibold shrink-0 ${arrowColor}`}
              title={deltaLabel || `Change vs. prior period: ${delta.direction === 'up' ? '+' : delta.direction === 'down' ? '−' : '±'}${Math.abs(delta.value).toFixed(2)}pp`}
              data-testid={`${testId}-delta`}
            >
              <ArrowIcon className="w-2.5 h-2.5" />
              <span className="tabular-nums">{Math.abs(delta.value).toFixed(2)}</span>
            </div>
          )}
        </div>
        {sub && <div className="text-[9px] text-slate-400 dark:text-slate-500 truncate">{sub}</div>}
        {sparkline && sparkline.length > 1 && (() => {
          // Feb 2026 — Context-aware end dot. Green = trend heading in
          // the "good" direction for this metric; red = bad direction;
          // gray = flat. Uses `delta.direction` (which was already
          // computed against the prior period) and `polarity`.
          let endDot = null;
          if (delta && delta.direction) {
            if (delta.direction === 'flat') {
              endDot = 'flat';
            } else if (polarity === 'up-good') {
              endDot = delta.direction === 'up' ? 'good' : 'bad';
            } else {
              // default 'up-bad'
              endDot = delta.direction === 'up' ? 'bad' : 'good';
            }
          }
          return (
            <Sparkline
              points={sparkline}
              width={60}
              height={12}
              endDot={endDot}
              className={`mt-0.5 ${iconColorClass}`}
            />
          );
        })()}
      </div>
    </div>
  );
}

export default function MacroContextStrip({ onOpenStock }) {
  const [data, setData] = useState(null);
  const [sectorPick, setSectorPick] = useState(null);
  const [flavor, setFlavor] = useState(null);
  // Feb 2026 — Mobile tap-to-explain. Desktop keeps native hover
  // tooltips; on touch devices we open a bottom-sheet modal with the
  // full tooltip text since hover doesn't work. State holds the
  // currently-open card's { label, tooltip, value, sub, personalNote }.
  const [openCard, setOpenCard] = useState(null);
  // Jul 7, 2026 — Mobile swipe hint. Shows a ChevronRight badge on
  // the right edge of the 2-row strip until the user scrolls it
  // horizontally for the first time. Dismissal persists in
  // localStorage so returning visitors don't see it again.
  const stripRef = useRef(null);
  // Oct 2026 — position-aware text hint (shared with Breakaway / Streaks).
  const [macroSwipeHint, updateMacroSwipeHint] = useSwipeHint(stripRef, [data]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/portfolio/macro-flavor`, {
          timeout: 3000,
          withCredentials: true,
        });
        if (!cancelled) setFlavor(r.data || null);
      } catch { /* anonymous or offline — modal falls back to generic */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Sep 27 2026 — user reported the Fed & Treasury Watch strip
    // rendering empty (title-only) ~50% of loads and steadily worse.
    // Root cause: the old 3 s timeout was tighter than /api/macro's
    // p95 (it aggregates 6+ warmed series and cold-cache runs can
    // touch 5-8 s), so a lot of first loads timed out and the
    // silent catch left `data` = null → `return null` → blank body.
    // Fix: 15 s timeout + one retry with a 1.5 s backoff. Both
    // attempts fail before we give up, and a soft refresh no
    // longer feels magic — it just gives us a warm cache the first
    // request would have gotten if we'd waited.
    (async () => {
      const attempt = async () => axios.get(`${API}/macro`, { timeout: 15000 });
      try {
        const r = await attempt();
        if (!cancelled) setData(r.data);
      } catch {
        if (cancelled) return;
        await new Promise((res) => setTimeout(res, 1500));
        if (cancelled) return;
        try {
          const r2 = await attempt();
          if (!cancelled) setData(r2.data);
        } catch {
          /* silently drop — no fallback */
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const t = data.series?.treasury_yields?.latest || {};
  const inf = data.series?.inflation?.latest || {};
  const exp = data.series?.inflation_expectations?.latest || {};
  const lab = data.series?.labor_market?.latest || {};
  const spy = data.series?.sp500_dividend_yield?.latest || {};
  const fc = data.series?.funding_conditions?.latest || {};
  const ffUpper = fc.fed_funds_target_upper;
  const ffLower = fc.fed_funds_target_lower;
  const ffPriorUpper = fc.prior_target_upper;
  const ffEffr = fc.effective_fed_funds_rate;
  const ffSofr = fc.secured_overnight_financing_rate;

  const y10 = t.yield_10_year;
  const y2 = t.yield_2_year;
  const curveSpread = (y10 != null && y2 != null) ? (y10 - y2) : null;
  const cpi = inf.cpi_year_over_year;
  const pceCore = inf.pce_core_year_over_year;
  const be10 = exp.market_10_year;
  const fwd5y5y = exp.forward_years_5_to_10;
  const unemp = lab.unemployment_rate;
  const lfpr = lab.labor_force_participation_rate;
  const spDivYield = spy.value;

  // Feb 2026 — compute deltas vs. prior period so each card can show
  // ↑/↓ direction. Backend attaches `prior` inside `latest` for each
  // series (see fed_economy._fetch_latest). Lookback windows:
  //   • treasury_yields → 30 trading days back
  //   • inflation / expectations / labor → 1 month back
  // For CPI YoY / Core PCE YoY the delta is "change in the YoY rate"
  // which is exactly what the market cares about (is inflation getting
  // better or worse?), not the raw CPI index level.
  const tPrior = t.prior || {};
  const infPrior = inf.prior || {};
  const expPrior = exp.prior || {};
  const labPrior = lab.prior || {};
  // Note: for the CPI/PCE YoY delta we compare against last month's
  // YoY reading. Since backend only returns raw index in `prior`, we
  // approximate the prior YoY by NOT having it — so we compute a
  // simpler month-over-month change in the RATE by comparing the
  // current YoY to the reported YoY of last month's underlying
  // series. Simpler: we don't have prior YoY calc'd in this payload,
  // so we omit CPI/PCE deltas for now and only show trends where the
  // raw value equals the displayed value.

  const mkDelta = (curr, prev, thresh = 0.005) => {
    if (curr == null || prev == null) return null;
    const d = curr - prev;
    if (Math.abs(d) < thresh) return { value: d, direction: 'flat' };
    return { value: d, direction: d > 0 ? 'up' : 'down' };
  };

  const y10Delta = mkDelta(y10, tPrior.yield_10_year);
  const ffDelta = ffPriorUpper != null ? mkDelta(ffUpper, ffPriorUpper) : (fc.prior?.fed_funds_target_upper != null ? mkDelta(ffUpper, fc.prior.fed_funds_target_upper) : null);
  const y2Delta = mkDelta(y2, tPrior.yield_2_year);
  const curveDelta = (curveSpread != null && tPrior.yield_10_year != null && tPrior.yield_2_year != null)
    ? mkDelta(curveSpread, tPrior.yield_10_year - tPrior.yield_2_year)
    : null;
  const be10Delta = mkDelta(be10, expPrior.market_10_year);
  const fwd5y5yDelta = mkDelta(fwd5y5y, expPrior.forward_years_5_to_10);
  const unempDelta = mkDelta(unemp, labPrior.unemployment_rate);
  const lfprDelta = mkDelta(lfpr, labPrior.labor_force_participation_rate);
  // CPI / PCE deltas — infPrior is last MONTH's raw index, so we
  // compute the level-to-level change and convert into a "did YoY
  // slow / speed up?" hint by comparing current YoY to a rough proxy
  // (the MoM change ANNUALIZED × 12). Skip if prior missing.
  const cpiDelta = (cpi != null && infPrior.cpi != null && inf.cpi != null && infPrior.cpi > 0)
    ? mkDelta(cpi, ((inf.cpi - infPrior.cpi) / infPrior.cpi * 100) * 12)
    : null;
  const pceCoreDelta = (pceCore != null && infPrior.pce_core != null && inf.pce_core != null && infPrior.pce_core > 0)
    ? mkDelta(pceCore, ((inf.pce_core - infPrior.pce_core) / infPrior.pce_core * 100) * 12)
    : null;

  // Feb 2026 — Extract sparkline arrays from each series' `history`.
  // Backend returns `history` oldest→newest (excluding the newest,
  // which is `latest` itself), so we append the current value to make
  // the sparkline end at "now". For rate/YoY series we render the
  // underlying rate/YoY numbers, not the raw indexes.
  const tHist = Array.isArray(t.history) ? t.history : [];
  const infHist = Array.isArray(inf.history) ? inf.history : [];
  const expHist = Array.isArray(exp.history) ? exp.history : [];
  const labHist = Array.isArray(lab.history) ? lab.history : [];

  const spark = (arr, key, currentVal) => {
    const pts = arr.map((row) => row?.[key]).filter((v) => typeof v === 'number');
    if (currentVal != null) pts.push(currentVal);
    return pts.length >= 2 ? pts : null;
  };
  // CPI YoY / Core PCE YoY history requires computing YoY at each
  // point from the raw index. `history` doesn't include the -13 month
  // observation needed to compute YoY[0], so we approximate by using
  // simple index-level trend which is close enough visually and shows
  // "hotter vs. cooler" direction correctly.
  const y10Spark = spark(tHist, 'yield_10_year', y10);
  const y2Spark = spark(tHist, 'yield_2_year', y2);
  const curveSpark = (() => {
    const pts = tHist
      .map((row) => (row?.yield_10_year != null && row?.yield_2_year != null)
        ? row.yield_10_year - row.yield_2_year
        : null)
      .filter((v) => typeof v === 'number');
    if (curveSpread != null) pts.push(curveSpread);
    return pts.length >= 2 ? pts : null;
  })();
  const cpiSpark = spark(infHist, 'cpi', inf.cpi);
  const pceCoreSpark = spark(infHist, 'pce_core', inf.pce_core);
  const be10Spark = spark(expHist, 'market_10_year', be10);
  const fwd5y5ySpark = spark(expHist, 'forward_years_5_to_10', fwd5y5y);
  const unempSpark = spark(labHist, 'unemployment_rate', unemp);
  const lfprSpark = spark(labHist, 'labor_force_participation_rate', lfpr);

  // Feb 2026 — Auto-generated "Macro Story" one-liner. Reads the
  // current tints + trends and produces a plain-English summary of
  // what the whole strip says together. Written from a dividend
  // investor's perspective. Defined LATER (after tint variables are
  // computed) — see `macroStory` right after the tint block below.

  const allNull = [y10, y2, curveSpread, cpi, pceCore, be10, fwd5y5y, unemp, lfpr, spDivYield]
    .every((v) => v == null);
  if (allNull) return null;

  const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(2)}%`);
  const fmtSpread = (v) => {
    if (v == null) return '—';
    const sign = v > 0 ? '+' : '';
    return `${sign}${v.toFixed(2)}%`;
  };

  // Feb 2026 — Yield curve state coloring.
  //  spread < 0        → INVERTED (red) — classic recession warning.
  //  0 ≤ spread < 0.10 → FLAT/NEAR (amber) — recovering or teetering.
  //  spread ≥ 0.10     → NORMAL — no special tint.
  const curveTint = curveSpread == null
    ? null
    : curveSpread < 0
      ? 'red'
      : curveSpread < 0.10
        ? 'amber'
        : null;

  // Feb 2026 — Additional state-driven coloring so the whole strip
  // reads as an at-a-glance macro dashboard. Thresholds picked from
  // widely-cited Fed / Wall Street consensus levels.

  // CPI YoY — >4% is real-income-eroding; 3-4% elevated; <2.5% target.
  const cpiTint = cpi == null
    ? null
    : cpi > 4
      ? 'red'
      : cpi > 3
        ? 'amber'
        : cpi < 2.5
          ? 'green'
          : null;

  // Core PCE YoY — Fed's actual target is 2%. >3% = hawkish territory
  // (rate-hike pressure); 2.5-3% elevated; <2.5% = Fed target
  // effectively hit / dovish.
  const pceCoreTint = pceCore == null
    ? null
    : pceCore > 3
      ? 'red'
      : pceCore > 2.5
        ? 'amber'
        : 'green';

  // Unemployment — <4% = full employment (green). 4-5% = neutral.
  // >5% = amber (recession threshold). >5.5% = red (recession under way).
  const unempTint = unemp == null
    ? null
    : unemp > 5.5
      ? 'red'
      : unemp > 5
        ? 'amber'
        : unemp < 4
          ? 'green'
          : null;

  // 10Y Breakeven Inflation — 2.0-2.5% = Fed credibility anchored (green).
  // >3% = expectations un-anchored (red); 2.5-3% = elevated (amber).
  const be10Tint = be10 == null
    ? null
    : be10 > 3
      ? 'red'
      : be10 > 2.5
        ? 'amber'
        : be10 >= 2.0
          ? 'green'
          : null;

  // 5Y5Y Forward Inflation — the Fed's #1 anchor gauge. 2.0-2.5% =
  // anchored (green). 2.5-2.75% = elevated (amber). >2.75% = Fed
  // credibility slipping (red).
  const fwd5y5yTint = fwd5y5y == null
    ? null
    : fwd5y5y > 2.75
      ? 'red'
      : fwd5y5y > 2.5
        ? 'amber'
        : fwd5y5y >= 2.0
          ? 'green'
          : null;

  // Feb 2026 — Auto-generated "Macro Story" one-liner. Reads the
  // current tints + trends and produces a plain-English summary of
  // what the whole strip says together. Written from a dividend
  // investor's perspective. Recomputes every render from live data.
  const buildMacroStory = () => {
    const parts = [];
    const infHot = cpiTint === 'red' || pceCoreTint === 'red';
    const infCool = cpiTint === 'green' && pceCoreTint !== 'red';
    if (infHot) parts.push('Inflation running hot');
    else if (infCool) parts.push('Inflation cooling toward Fed target');
    else if (cpiTint === 'amber' || pceCoreTint === 'amber') parts.push('Inflation elevated but not extreme');

    const anchorGood = be10Tint === 'green' && fwd5y5yTint === 'green';
    const anchorSlipping = be10Tint === 'red' || fwd5y5yTint === 'red';
    if (anchorGood && infHot) parts.push('but long-term Fed credibility still intact');
    else if (anchorGood) parts.push('Fed inflation anchor holding');
    else if (anchorSlipping) parts.push('Fed credibility slipping');

    if (curveTint === 'red') parts.push('⚠️ yield curve INVERTED — recession warning');
    else if (curveTint === 'amber') parts.push('yield curve flat — watch closely');

    if (unempTint === 'red') parts.push('labor market in recessionary territory');
    else if (unempTint === 'amber') parts.push('unemployment rising toward recession threshold');
    else if (unempTint === 'green' && !infHot) parts.push('labor market strong');

    let action = '';
    let playbookKey = 'neutral';
    if (curveTint === 'red' || unempTint === 'red') {
      action = 'Favor defensive dividend Aristocrats and low-payout-ratio growers; avoid highly-leveraged REITs and cyclicals.';
      playbookKey = 'recession';
    } else if (infHot && anchorSlipping) {
      action = 'Rotate toward real-asset dividend payers (energy, materials, infrastructure) — inflation is hot AND the Fed anchor is slipping.';
      playbookKey = 'stagflation';
    } else if (infHot) {
      action = anchorGood
        ? 'Dividend growers with pricing power (Aristocrats, Kings) outperform. Avoid long-duration utilities until Fed pivots.'
        : 'Dividend growers with pricing power (Aristocrats, Kings) outperform; keep a real-asset sleeve (energy, materials) while long-run inflation expectations drift. Avoid long-duration utilities until the Fed pivots.';
      playbookKey = 'hotInflation';
    } else if (infCool && anchorGood) {
      action = 'Dovish tailwind for rate-sensitive dividend stocks (utilities, REITs, telecoms).';
      playbookKey = 'rateHikeTop';
    } else if (anchorSlipping) {
      action = 'Rotate toward real-asset dividend payers (energy, materials, infrastructure).';
      playbookKey = 'stagflation';
    } else {
      action = 'Neutral macro backdrop — stock selection matters more than sector tilts. Lean on quality: durable payout coverage, low leverage, steady dividend growth.';
    }

    if (parts.length === 0) return null;
    return {
      headline: parts.join(', ') + '.',
      action,
      playbookKey,
    };
  };
  const macroStory = buildMacroStory();

  // Feb 2026 — Historical regime matcher. Compares the current tint
  // fingerprint to a small hardcoded set of known macro regimes and
  // surfaces the closest match with approximate dividend-index return
  // over that window. Not a full historical database — just a curated
  // "here's what happened last time we were in a state like this" for
  // portfolio-confidence context.
  //
  // Fingerprint order: [cpiTint, pceCoreTint, be10Tint, fwd5y5yTint,
  // curveTint, unempTint]. Values are 'red' | 'amber' | 'green' | null.
  const HISTORICAL_REGIMES = [
    {
      fp: ['red', 'red', 'green', 'green', null, 'green'],
      name: 'Late-cycle inflation squeeze (2022–2023)',
      playbook: 'hotInflation',
      window: 'Mar 2022 – Jul 2023',
      aristocrats: '+8.4%',
      spy: '+2.1%',
      note: 'Aristocrats outperformed SPY as investors rotated from growth into dividend-paying blue chips. Consumer Staples and Energy led; long-duration Utilities lagged.',
    },
    {
      fp: ['red', 'red', 'red', 'amber', 'red', 'amber'],
      name: '1970s stagflation echo',
      playbook: 'stagflation',
      window: 'Nearest analog: 2007–2008',
      aristocrats: '−7.2%',
      spy: '−18.5%',
      note: 'Aristocrats held up better than the broad market but still lost value. Energy + Consumer Staples were the only sector-level winners.',
    },
    {
      fp: ['green', 'green', 'green', 'green', null, 'green'],
      name: 'Goldilocks (2013–2015, 2017)',
      playbook: 'goldilocks',
      window: 'Multiple 12-month windows',
      aristocrats: '+11.8%',
      spy: '+13.6%',
      note: 'Dividend growth broad-based. All sectors participated. Ideal environment for compounding Aristocrats + Kings positions.',
    },
    {
      fp: [null, null, null, null, 'red', 'red'],
      name: 'Recession onset (2008, 2020)',
      playbook: 'recession',
      window: '~6 months from curve inversion',
      aristocrats: '−12.4%',
      spy: '−18.9%',
      note: 'Defensive Aristocrats (JNJ, PG, KO, WMT) massively outperformed. High-payout BDCs / mREITs cut or suspended dividends. Cash-heavy Kings shone.',
    },
    {
      fp: ['amber', 'amber', 'green', 'green', null, 'green'],
      name: 'Rate-hike cycle top (2018–2019)',
      playbook: 'rateHikeTop',
      window: 'Aug 2018 – Feb 2019',
      aristocrats: '+11.8%',
      spy: '+4.1%',
      note: 'Aristocrats crushed SPY as the Fed pivoted dovish. Consumer Staples +14%, Utilities +11%, Financials −3%.',
    },
  ];
  const currentFingerprint = [cpiTint, pceCoreTint, be10Tint, fwd5y5yTint, curveTint, unempTint];
  const fpDistance = (a, b) => a.reduce((s, v, i) => s + (v === b[i] ? 0 : 1), 0);
  const bestRegime = HISTORICAL_REGIMES
    .map((r) => ({ ...r, d: fpDistance(currentFingerprint, r.fp) }))
    .sort((a, b) => a.d - b.d)[0];
  // Only surface if fingerprint distance is ≤3 out of 6 (meaningful match)
  const historicalMatch = bestRegime || null;
  const sectorPlaybook = SECTOR_PLAYBOOKS[(historicalMatch && historicalMatch.d <= 3 ? historicalMatch.playbook : null) || macroStory?.playbookKey] || null;

  // Feb 2026 — Per-card "What it means for YOUR portfolio" note. Reads
  // the user's holdings flavor and returns a 1-2 sentence personalized
  // impact statement. Called lazily when a card modal opens.
  const buildPersonalNote = (cardKey) => {
    if (!flavor || !flavor.total_holdings) return null;
    const b = flavor.top_tickers_by_bucket || {};
    const list = (arr) => arr && arr.length ? arr.join(', ') : null;
    const reits = list(b.reits);
    const bdcs = list(b.bdcs);
    const banks = list(b.banks);
    const utils = list(b.utilities);
    const energy = list(b.energy);
    const staples = list(b.staples);
    const tech = list(b.tech);
    const consumer = list(b.consumer);
    const leveraged = list(b.leveraged);
    switch (cardKey) {
      case 'treasury_10y': {
        const rising = y10Delta?.direction === 'up';
        if (reits && rising) return `Your REIT holding${b.reits.length > 1 ? 's' : ''} (${reits}) face${b.reits.length > 1 ? '' : 's'} pressure as long-duration income competes with the rising risk-free rate. Watch for FFO revisions.`;
        if (utils && rising) return `Your Utilities holding${b.utilities.length > 1 ? 's' : ''} (${utils}) may lag as capital-intensive dividend payers get squeezed by higher borrowing costs.`;
        if (reits && !rising) return `Falling 10Y is a tailwind for your REITs (${reits}) — cheaper refinancing + more attractive relative yield.`;
        return null;
      }
      case 'fed_funds': {
        const hiked = ffDelta?.direction === 'up';
        const cut = ffDelta?.direction === 'down';
        if (hiked && (banks || bdcs)) return `A Fed hike lifts floating-rate income: your ${banks ? `bank${b.banks.length > 1 ? 's' : ''} (${banks})` : ''}${banks && bdcs ? ' and ' : ''}${bdcs ? `BDC${b.bdcs.length > 1 ? 's' : ''} (${bdcs})` : ''} should see wider margins within a quarter.`;
        if (hiked && (reits || utils)) return `Higher policy rates squeeze long-duration payers — watch refinancing costs on ${reits || utils}.`;
        if (cut && (reits || utils)) return `A Fed cut is a tailwind for your rate-sensitive ${reits ? `REITs (${reits})` : `utilities (${utils})`} — cheaper debt, more attractive relative yield.`;
        if (cut && bdcs) return `Cuts trim floating-rate NII for your BDC${b.bdcs.length > 1 ? 's' : ''} (${bdcs}) — check dividend coverage next quarter.`;
        return null;
      }
      case 'treasury_2y': {
        const rising = y2Delta?.direction === 'up';
        if (banks && rising) return `Your bank holding${b.banks.length > 1 ? 's' : ''} (${banks}) may benefit from rising short-rates → wider net interest margins.`;
        if (banks && !rising) return `Falling 2Y compresses net interest margins for your banks (${banks}).`;
        return null;
      }
      case 'yield_curve': {
        if (curveTint === 'red') {
          const defensive = staples || utils;
          return defensive
            ? `Inverted curve historically pre-empts recessions. Your defensive holdings (${defensive}) tend to outperform. Consider trimming cyclicals if any.`
            : `Inverted curve historically pre-empts recessions. Your portfolio leans cyclical — consider rotating into defensive Aristocrats (staples, healthcare).`;
        }
        if (banks) return `Steepening curve is a tailwind for your banks (${banks}) — higher long-rate + stable short-rate = wider NIM.`;
        return null;
      }
      case 'cpi':
      case 'pce_core': {
        if (cpiTint === 'red' || pceCoreTint === 'red') {
          const pricers = energy || staples;
          if (pricers) return `Your pricing-power names (${pricers}) can pass through inflation. Watch consumer-discretionary + long-duration income for margin pressure.`;
          if (consumer) return `Your consumer discretionary holding${b.consumer.length > 1 ? 's' : ''} (${consumer}) face${b.consumer.length > 1 ? '' : 's'} margin pressure — customers get squeezed on non-essentials first.`;
        }
        return null;
      }
      case 'breakeven_10y':
      case 'forward_5y5y': {
        if (be10Tint === 'red' || fwd5y5yTint === 'red') {
          if (energy) return `Fed credibility slipping is bullish for real-asset dividend payers like your energy names (${energy}).`;
        }
        return null;
      }
      case 'unemployment': {
        if (unempTint === 'red' || unempTint === 'amber') {
          const at_risk = consumer || (reits ? `retail REITs like ${reits}` : null);
          if (at_risk) return `Rising unemployment weighs on your consumer-facing holdings (${at_risk}). Historically these see earnings revisions first.`;
        }
        if (unempTint === 'green' && staples) return `Full-employment supports your consumer staples (${staples}) via steady household spending.`;
        return null;
      }
      case 'lfpr': return null; // No strong per-holding signal
      case 'sp500_yield': {
        return `Your portfolio contains ${flavor.total_holdings} holding${flavor.total_holdings === 1 ? '' : 's'}. If most yield above 0.97%, you're being paid extra for taking single-name risk vs. holding SPY.`;
      }
      default:
        return null;
    }
  };

  return (
    <div data-testid="macro-strip-wrap">
      {/* Feb 2026 — Mobile: 2-row horizontal swipe strip (cards bleed
          past the right edge for an obvious "swipe me" affordance).
          sm+ and lg+: normal grid, unchanged from prior release. */}
      <div className="relative">
      <div
        ref={stripRef}
        onScroll={updateMacroSwipeHint}
        className="
          grid gap-2
          grid-flow-col grid-rows-2 auto-cols-[46%]
          overflow-x-auto snap-x snap-mandatory scroll-smooth
          -mx-4 px-4 pb-1
          sm:grid-flow-row sm:grid-rows-none sm:auto-cols-auto
          sm:overflow-x-visible sm:mx-0 sm:px-0 sm:pb-0
          sm:grid-cols-6
          [scrollbar-width:none] [-ms-overflow-style:none]
          [&::-webkit-scrollbar]:hidden
        "
        data-testid="macro-context-strip"
      >
      {ffUpper != null && (
        <Cell
          icon={Landmark}
          label="Fed Funds Target"
          value={`${Number(ffLower).toFixed(2)}–${Number(ffUpper).toFixed(2)}%`}
          sub={fc.target_changed_on ? `since ${fc.target_changed_on}` : fc.date}
          accent="amber"
          delta={ffDelta}
          deltaLabel={ffDelta && ffDelta.direction !== 'flat'
            ? `${ffDelta.direction === 'up' ? 'HIKED' : 'CUT'} ${Math.abs(ffDelta.value * 100).toFixed(0)}bp${fc.target_changed_on ? ` on ${fc.target_changed_on}` : ''} — was ${Number(ffPriorUpper ?? fc.prior?.fed_funds_target_upper).toFixed(2)}% upper bound`
            : 'Unchanged over the last ~6 weeks'}
          cardKey="fed_funds"
          onTap={setOpenCard}
          testId="macro-cell-fed-funds"
          tooltip={`Federal Funds Target Range — the policy rate the FOMC sets 8× a year; every other short rate keys off it. Effective rate ${ffEffr != null ? ffEffr.toFixed(2) + '%' : '—'} · SOFR ${ffSofr != null ? ffSofr.toFixed(2) + '%' : '—'}. Floating-rate payers (CLOs, BDCs, bank NIM) earn more when this rises; long-duration dividend stocks (utilities, REITs) feel the squeeze. Source: FRED via Massive — target changes post the morning after the FOMC statement.`}
        />
      )}
      {y10 != null && (
        <Cell
          icon={TrendingUp}
          label="10Y Treasury"
          value={fmtPct(y10)}
          sub={t.date}
          accent="blue"
          delta={y10Delta}
          sparkline={y10Spark}
          deltaLabel={y10Delta ? `${y10Delta.direction === 'up' ? 'Rising' : y10Delta.direction === 'down' ? 'Falling' : 'Flat'} ${Math.abs(y10Delta.value).toFixed(2)}pp vs. ~30 days ago` : null}
          cardKey="treasury_10y"
          onTap={setOpenCard}
          testId="macro-cell-treasury"
          tooltip={`10-Year U.S. Treasury Yield — the annual interest paid by the U.S. government on a 10-year bond. The de-facto "risk-free rate" that every dividend yield is measured against. When the 10Y is above your stock's yield, bonds pay more than your dividend for zero business risk.`}
        />
      )}
      {y2 != null && (
        <Cell
          icon={Landmark}
          label="2Y Treasury"
          value={fmtPct(y2)}
          sub={t.date}
          accent="cyan"
          delta={y2Delta}
          sparkline={y2Spark}
          deltaLabel={y2Delta ? `${y2Delta.direction === 'up' ? 'Rising' : y2Delta.direction === 'down' ? 'Falling' : 'Flat'} ${Math.abs(y2Delta.value).toFixed(2)}pp vs. ~30 days ago — market pricing in ${y2Delta.direction === 'up' ? 'MORE Fed hikes / fewer cuts' : y2Delta.direction === 'down' ? 'MORE cuts / fewer hikes' : 'no policy change'}` : null}
          cardKey="treasury_2y"
          onTap={setOpenCard}
          testId="macro-cell-treasury-2y"
          tooltip={`2-Year U.S. Treasury Yield — the closest market proxy to the Fed Funds Rate. Tracks Fed policy expectations tighter than any other maturity. Rising 2Y = market pricing in more hikes; falling = market pricing in cuts.`}
        />
      )}
      {curveSpread != null && (
        <Cell
          icon={LineChart}
          label="Yield Curve"
          value={fmtSpread(curveSpread)}
          sub="10Y − 2Y"
          accent="emerald"
          stateTint={curveTint}
          delta={curveDelta}
          sparkline={curveSpark}
          polarity="up-good"
          deltaLabel={curveDelta ? `Spread ${curveDelta.direction === 'up' ? 'steepening' : curveDelta.direction === 'down' ? 'flattening / inverting' : 'unchanged'} ${Math.abs(curveDelta.value).toFixed(2)}pp vs. ~30 days ago` : null}
          cardKey="yield_curve"
          onTap={setOpenCard}
          testId="macro-cell-yield-curve"
          tooltip={
            curveTint === 'red'
              ? `⚠️ INVERTED YIELD CURVE — 10Y is paying LESS than 2Y. The bond market is pricing in a recession. Every US recession since 1970 was preceded by a curve inversion (typically 6-18 months in advance). Watch cyclicals, banks, and dividend cuts closely.`
              : curveTint === 'amber'
                ? `⚡ FLAT YIELD CURVE — spread of ${fmtSpread(curveSpread)} means 10Y and 2Y are almost identical. Either an inversion is unwinding (economy recovering) or the curve is about to flip (recession warning). Read with 2Y direction.`
                : `Yield Curve Spread (10Y − 2Y) — the classic recession indicator. Negative (inverted) = markets expect the Fed to cut rates because a slowdown is coming (has preceded every US recession since 1970). Positive & steepening = growth signal, banks profit, cyclicals lead.`
          }
        />
      )}
      {cpi != null && (
        <Cell
          icon={DollarSign}
          label="CPI YoY"
          value={fmtPct(cpi)}
          sub={inf.date}
          accent="amber"
          stateTint={cpiTint}
          delta={cpiDelta}
          sparkline={cpiSpark}
          deltaLabel={cpiDelta ? `Trending ${cpiDelta.direction === 'up' ? 'HOTTER' : cpiDelta.direction === 'down' ? 'COOLER' : 'FLAT'} vs. last month (annualized MoM proxy)` : null}
          cardKey="cpi"
          onTap={setOpenCard}
          testId="macro-cell-cpi"
          tooltip={
            cpiTint === 'red'
              ? `⚠️ CPI YoY at ${fmtPct(cpi)} — inflation above 4% is actively eroding real income. Your dividend yield must beat CPI just to stand still. Fed likely to stay hawkish.`
              : cpiTint === 'amber'
                ? `⚡ CPI YoY at ${fmtPct(cpi)} — elevated (3-4% band). Above Fed's target but not yet crisis level. Dividend growers with pricing power outperform in this zone.`
                : cpiTint === 'green'
                  ? `✓ CPI YoY at ${fmtPct(cpi)} — below 2.5% target zone. Real dividend income is growing. Fed dovish tailwind for rate-sensitive dividend stocks (utilities, REITs).`
                  : `Consumer Price Index, year-over-year — how much prices have risen in the last 12 months. If your dividend yield is below CPI, your income is losing purchasing power. Target: dividend yield > CPI + risk-free rate.`
          }
        />
      )}
      {pceCore != null && (
        <Cell
          icon={Activity}
          label="Core PCE YoY"
          value={fmtPct(pceCore)}
          sub={inf.date}
          accent="rose"
          stateTint={pceCoreTint}
          delta={pceCoreDelta}
          sparkline={pceCoreSpark}
          deltaLabel={pceCoreDelta ? `Trending ${pceCoreDelta.direction === 'up' ? 'HOTTER' : pceCoreDelta.direction === 'down' ? 'COOLER' : 'FLAT'} vs. last month (annualized MoM proxy) — Fed watching this closely` : null}
          cardKey="pce_core"
          onTap={setOpenCard}
          testId="macro-cell-pce-core"
          tooltip={
            pceCoreTint === 'red'
              ? `⚠️ Core PCE at ${fmtPct(pceCore)} — above 3% is Fed HAWKISH territory. Rate hikes back on the table. Long-duration dividend stocks (utilities, telecoms) face headwinds.`
              : pceCoreTint === 'amber'
                ? `⚡ Core PCE at ${fmtPct(pceCore)} — elevated (2.5-3%). Above Fed's 2% target but progress visible. Fed on hold, not cutting yet.`
                : `✓ Core PCE at ${fmtPct(pceCore)} — at or below Fed's 2% target zone. Rate cuts on the table. Bullish for dividend stocks broadly, especially rate-sensitive sectors.`
          }
        />
      )}
      {be10 != null && (
        <Cell
          icon={Target}
          label="10Y Breakeven"
          value={fmtPct(be10)}
          sub={exp.date}
          accent="violet"
          stateTint={be10Tint}
          delta={be10Delta}
          sparkline={be10Spark}
          deltaLabel={be10Delta ? `${be10Delta.direction === 'up' ? 'Rising' : be10Delta.direction === 'down' ? 'Falling' : 'Flat'} ${Math.abs(be10Delta.value).toFixed(2)}pp vs. last month` : null}
          cardKey="breakeven_10y"
          onTap={setOpenCard}
          testId="macro-cell-breakeven"
          tooltip={
            be10Tint === 'red'
              ? `⚠️ 10Y Breakeven at ${fmtPct(be10)} — above 3% means bond market has lost faith in Fed's inflation control. Real dividend yields are shrinking.`
              : be10Tint === 'amber'
                ? `⚡ 10Y Breakeven at ${fmtPct(be10)} — elevated (2.5-3%). Bond market pricing in above-target inflation for a decade.`
                : be10Tint === 'green'
                  ? `✓ 10Y Breakeven at ${fmtPct(be10)} — anchored in the 2.0-2.5% Fed-credibility zone. Bond market trusts long-run inflation control.`
                  : `10-Year Breakeven Inflation — the bond market's forecast of average annual inflation over the next 10 years (10Y nominal yield minus 10Y TIPS yield). If breakeven is 2.3%, bond investors expect ~2.3%/yr inflation. Compare to CPI YoY: if breakeven < CPI, the market thinks inflation is peaking.`
          }
        />
      )}
      {fwd5y5y != null && (
        <Cell
          icon={Anchor}
          label="5Y5Y Forward"
          value={fmtPct(fwd5y5y)}
          sub={exp.date}
          accent="fuchsia"
          stateTint={fwd5y5yTint}
          delta={fwd5y5yDelta}
          sparkline={fwd5y5ySpark}
          deltaLabel={fwd5y5yDelta ? `${fwd5y5yDelta.direction === 'up' ? 'Rising' : fwd5y5yDelta.direction === 'down' ? 'Falling' : 'Flat'} ${Math.abs(fwd5y5yDelta.value).toFixed(2)}pp vs. last month` : null}
          cardKey="forward_5y5y"
          onTap={setOpenCard}
          testId="macro-cell-5y5y-forward"
          tooltip={
            fwd5y5yTint === 'red'
              ? `⚠️ 5Y5Y Forward at ${fmtPct(fwd5y5y)} — Fed's long-term inflation anchor is SLIPPING. When this number drifts above 2.75%, Fed credibility is on the line.`
              : fwd5y5yTint === 'amber'
                ? `⚡ 5Y5Y Forward at ${fmtPct(fwd5y5y)} — mildly elevated. Watch for further drift; near 2.75% Fed hawkishness returns.`
                : fwd5y5yTint === 'green'
                  ? `✓ 5Y5Y Forward at ${fmtPct(fwd5y5y)} — anchored in the 2.0-2.5% zone. Fed's inflation credibility intact. Long-duration dividend stocks safe.`
                  : `5-Year, 5-Year Forward Inflation Expectations — the Fed's #1 long-term inflation anchor gauge. The average inflation the market expects for the 5 years starting 5 years from now. Anchored near 2.2% = Fed credibility intact. Drifting = policy trouble.`
          }
        />
      )}
      {unemp != null && (
        <Cell
          icon={Users}
          label="Unemployment"
          value={fmtPct(unemp)}
          sub={lab.date}
          accent="slate"
          stateTint={unempTint}
          delta={unempDelta}
          sparkline={unempSpark}
          deltaLabel={unempDelta ? `${unempDelta.direction === 'up' ? 'Rising' : unempDelta.direction === 'down' ? 'Falling' : 'Flat'} ${Math.abs(unempDelta.value).toFixed(2)}pp vs. last month${unempDelta.direction === 'up' ? ' — labor market softening' : unempDelta.direction === 'down' ? ' — labor market tightening' : ''}` : null}
          cardKey="unemployment"
          onTap={setOpenCard}
          testId="macro-cell-unemployment"
          tooltip={
            unempTint === 'red'
              ? `⚠️ Unemployment at ${fmtPct(unemp)} — above 5.5% is recessionary territory. Consumer-facing dividend stocks (retail REITs, restaurants, autos) face earnings pressure.`
              : unempTint === 'amber'
                ? `⚡ Unemployment at ${fmtPct(unemp)} — above 5% is the recession-warning threshold. Fed likely to cut rates (bullish rate-sensitive dividend stocks), but watch consumer names.`
                : unempTint === 'green'
                  ? `✓ Unemployment at ${fmtPct(unemp)} — full-employment zone (below 4%). Strong consumer spending underpins dividend growers.`
                  : `U.S. unemployment rate. Historically <4% = tight labor market (wage pressure, Fed hawkish); >5% = slack (Fed dovish, rate cuts likely). A leading indicator for consumer-facing dividend stocks (retail REITs, restaurants, autos).`
          }
        />
      )}
      {lfpr != null && (
        <Cell
          icon={UserCheck}
          label="Participation"
          value={fmtPct(lfpr)}
          sub={lab.date}
          accent="blue"
          delta={lfprDelta}
          sparkline={lfprSpark}
          polarity="up-good"
          deltaLabel={lfprDelta ? `${lfprDelta.direction === 'up' ? 'Rising' : lfprDelta.direction === 'down' ? 'Falling' : 'Flat'} ${Math.abs(lfprDelta.value).toFixed(2)}pp vs. last month` : null}
          cardKey="lfpr"
          onTap={setOpenCard}
          testId="macro-cell-lfpr"
          tooltip={`Labor Force Participation Rate — the share of working-age Americans either employed or actively looking. Pairs with the unemployment rate: rising participation = slack returning, Fed dovish; falling participation = wage pressure ahead. A "shadow" tightness gauge.`}
        />
      )}
      {spDivYield != null && (
        <Cell
          icon={PiggyBank}
          label="S&P 500 Yield"
          value={fmtPct(spDivYield)}
          sub="via SPY ETF"
          accent="emerald"
          cardKey="sp500_yield"
          onTap={setOpenCard}
          testId="macro-cell-sp500-yield"
          tooltip={`S&P 500 dividend yield (proxied via SPY ETF) — the direct market baseline that every individual dividend stock is measured against. If a stock yields well above the S&P baseline, you're being paid extra for taking single-name risk. Currently near historical lows in favor of buybacks.`}
        />
      )}
      </div>
      </div>
      <SwipeHint hint={macroSwipeHint} testId="macro-swipe-hint" />
      {/* Feb 2026 — Macro Story: auto-generated plain-English one-liner
          derived from the current tint states + a dividend-investor
          takeaway. Sits directly below the 10-card strip so the story
          is always tied to the data above it. */}
      {macroStory && (
        <div
          className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg
            bg-gradient-to-r from-violet-50/80 via-blue-50/80 to-emerald-50/80
            dark:from-violet-500/[0.08] dark:via-blue-500/[0.08] dark:to-emerald-500/[0.08]
            border border-slate-200 dark:border-slate-700/60
            shadow-sm"
          data-testid="macro-story"
        >
          <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-violet-600 dark:text-violet-400" />
          <div className="text-xs leading-relaxed min-w-0 flex-1">
            <div className="font-semibold text-slate-900 dark:text-slate-100" data-testid="macro-story-headline">{macroStory.headline}</div>
            {historicalMatch && (
              <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-400" data-testid="macro-story-historical">
                <span className="font-semibold text-violet-700 dark:text-violet-300">History says: </span>
                Closest match — <span className="font-medium text-slate-800 dark:text-slate-200">{historicalMatch.name}</span>
                <span
                  className={`ml-1.5 inline-flex items-center px-1.5 py-0 rounded-full text-[9px] font-bold uppercase tracking-wider align-middle ${
                    historicalMatch.d <= 1
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                      : historicalMatch.d === 2
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                        : 'bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200'
                  }`}
                  data-testid="macro-story-historical-confidence"
                  title={
                    historicalMatch.d <= 1
                      ? `Strong match — ${historicalMatch.d}/6 tint mismatches. Historical read carries meaningful weight.`
                      : historicalMatch.d === 2
                        ? `Approximate match — ${historicalMatch.d}/6 tint mismatches. Directionally useful but not a perfect analog.`
                        : historicalMatch.d === 3
                          ? `Loose match — ${historicalMatch.d}/6 tint mismatches. Take the historical read as flavor, not prediction.`
                          : `Weak match — ${historicalMatch.d}/6 tint mismatches. No clean historical analog; the playbook below follows today's chip readings instead.`
                  }
                >
                  {historicalMatch.d <= 1 ? 'Strong' : historicalMatch.d === 2 ? 'Approximate' : historicalMatch.d === 3 ? 'Loose' : 'Weak'}
                </span>
                {' '}({historicalMatch.window}) · Aristocrats <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{historicalMatch.aristocrats}</span> vs SPY <span className="font-mono">{historicalMatch.spy}</span>.
              </div>
            )}
            {sectorPlaybook && (
              <div className="mt-2.5" data-testid="macro-sector-playbook">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Sector Playbook <span className="normal-case tracking-normal font-semibold text-sky-600 dark:text-sky-400">· click or tap a sector for top-quality examples</span></div>
                <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  <PlaybookList title="Favor" tone="emerald" icon={CheckCircle2} items={sectorPlaybook.favor} testId="macro-playbook-favor" onPick={(item, tone) => setSectorPick({ item, tone })} />
                  <PlaybookList title="Avoid" tone="rose" icon={Ban} items={sectorPlaybook.avoid} testId="macro-playbook-avoid" onPick={(item, tone) => setSectorPick({ item, tone })} />
                </div>
              </div>
            )}
            <div className="mt-2 pt-1.5 border-t border-slate-200/60 dark:border-slate-700/40 text-[11px] text-slate-600 dark:text-slate-300" data-testid="macro-story-action">
              <div className="font-semibold text-slate-800 dark:text-slate-200">{macroStory.action}</div>
              {historicalMatch && <div className="mt-1 text-slate-500 dark:text-slate-400" data-testid="macro-story-note">{historicalMatch.note}</div>}
            </div>
          </div>
        </div>
      )}
      {sectorPick && (
        <SectorExamplesModal
          item={sectorPick.item}
          tone={sectorPick.tone}
          onClose={() => setSectorPick(null)}
          onOpenStock={(t, tab) => onOpenStock?.(t, tab || null)}
        />
      )}
      {/* Feb 2026 — Tap-to-explain modal. Fires when a user taps a
          macro card on ANY device (desktop keeps native hover tooltips
          too, but the modal is the primary explanation vehicle on
          touch devices where hover doesn't exist). Jul 6, 2026 —
          centered on both mobile and desktop (was bottom-sheet on
          mobile, but the Emergent badge in the bottom-right corner
          of the production deploy was covering the last two lines
          of tooltip text). */}
      {openCard && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setOpenCard(null)}
          data-testid="macro-card-modal-backdrop"
        >
          <div
            className="w-full max-w-md max-h-[85vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="macro-card-modal"
          >
            <button
              type="button"
              onClick={() => setOpenCard(null)}
              className="absolute top-3 right-3 p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              data-testid="macro-card-modal-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
                {openCard.label}
              </div>
              <div className="font-mono text-2xl font-bold text-slate-900 dark:text-slate-50 tabular-nums mt-0.5">
                {openCard.value}
              </div>
              {openCard.sub && (
                <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{openCard.sub}</div>
              )}
            </div>
            <div className="text-sm leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-line">
              {openCard.tooltip}
            </div>
            {/* Feb 2026 — Personalized "What it means for YOUR
                portfolio" note. Only renders when the user has
                holdings AND the current card has a useful signal
                for this portfolio composition. */}
            {(() => {
              const note = buildPersonalNote(openCard.cardKey);
              const buckets = flavor?.top_tickers_by_bucket || {};
              const counts = flavor?.bucket_counts || {};
              const dirFor = (key) => {
                switch (key) {
                  case 'fed_funds': return ffDelta?.direction || 'flat';
                  case 'treasury_10y': return y10Delta?.direction || 'flat';
                  case 'treasury_2y': return y2Delta?.direction || 'flat';
                  case 'yield_curve': return curveTint || 'green';
                  case 'cpi': return cpiTint || 'amber';
                  case 'pce_core': return pceCoreTint || 'amber';
                  case 'breakeven_10y': return be10Tint || 'amber';
                  case 'forward_5y5y': return fwd5y5yTint || 'amber';
                  case 'unemployment': return unempTint || 'amber';
                  case 'lfpr': return unempTint || 'amber';
                  default: return null;
                }
              };
              const table = IMPACT_MATRIX[openCard.cardKey]?.[dirFor(openCard.cardKey)] || null;
              const pick = (side) => Object.entries((table && table[side]) || {})
                .filter(([bucket]) => (buckets[bucket] || []).length)
                .map(([bucket, why]) => [bucket, why, buckets[bucket], counts[bucket] || buckets[bucket].length]);
              const benefit = table ? pick('benefit') : [];
              const headwind = table ? pick('headwind') : [];
              const hasHoldings = !!flavor?.total_holdings;
              if (!hasHoldings && !note) return null;
              return (
                <div
                  className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700"
                  data-testid="macro-card-modal-personal-note"
                >
                  <div className="text-[10px] uppercase tracking-wider text-violet-600 dark:text-violet-400 font-bold mb-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    What it means for YOUR portfolio
                  </div>
                  {note && (
                    <div className="text-xs leading-relaxed text-slate-700 dark:text-slate-200">
                      {note}
                    </div>
                  )}
                  {hasHoldings && table && <PortfolioImpactLists benefit={benefit} headwind={headwind} />}
                  {hasHoldings && !table && !note && (
                    <div className="text-xs text-slate-500 dark:text-slate-400">This reading is neutral for the sectors you hold right now.</div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
